import {
  AfterViewInit,
  Component,
  HostListener,
  HostBinding,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

import { CoreConfigService } from "@core/services/config.service";
import { getMenuLabel, isImageIcon, isLucideIcon, lucideIconName } from "@core/utils";
import { CoreMenuService } from "@core/components/core-menu/core-menu.service";

import { User } from "@common/models";
import { AuthenticationService } from "@common/services";

@Component({
    selector: "[core-menu-horizontal-collapsible]",
    templateUrl: "./collapsible.component.html",
    standalone: false
})
export class CoreMenuHorizontalCollapsibleComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  coreConfig: any;
  currentUser: User;
  isShow = false;

  // Conditionally add the active classes if UrlInChildren
  @HostBinding("class.active")
  @HostBinding("class.open")
  @HostBinding("class.sidebar-group-active")
  public isActive = false;

  @Input()
  item: any;

  /** Expone el helper al template: el icono puede ser clase o imagen */
  readonly isImageIcon = isImageIcon;

  /** Los iconos elegidos del catálogo se guardan como 'lucide:<nombre>' */
  readonly isLucideIcon = isLucideIcon;
  readonly lucideIconName = lucideIconName;

  /** Título en el idioma activo (clave i18n o texto de la base) */
  get label(): string {
    return getMenuLabel(this.item, this._translateService);
  }

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreMenuService} _coreMenuService
   */
  constructor(
    private el: ElementRef,
    private _router: Router,
    private _coreConfigService: CoreConfigService,
    private _coreMenuService: CoreMenuService,
    private _authenticationService: AuthenticationService,
    private _translateService: TranslateService
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
      });

    // Subscribe to the current menu changes
    // Subscribe to the current menu changes
    this._coreMenuService.onMenuChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => { });
    this._authenticationService.onUserValueChanged
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((resp) => {
        if (resp) {
          this.currentUser = resp;
        }
      });

    // Listen for router events and expand
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe((event: NavigationEnd) => {
        // Confirm if the urlAfterRedirects can be found in one of the children of this item
        if (this.confirmUrlInChildren(this.item, event.urlAfterRedirects)) {
          this.isActive = true;
        } else {
          this.isActive = false;
        }
      });

    // Check if the url can be found in one of the children of this item
    // Required for onInit case (i.e switching theme customizer menu layout)
    if (this.confirmUrlInChildren(this.item, this._router.url)) {
      this.isActive = true;
    } else {
      this.isActive = false;
    }
  }

  ngAfterViewInit(): void {
    // Bootstrap's display:none can be overridden by the slideIn animation on first load.
    // Force-hide nested submenus with inline !important so visibility is JS-controlled.
    // Only applies to NESTED collapsibles (parent is a .dropdown-menu), NOT top-level ones
    // (Otros, Configuración) whose parent is the navbar-nav and should open downward via CSS.
    if (this.isNestedSubmenu()) {
      const submenu = this.el.nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
      if (submenu) {
        submenu.style.setProperty('display', 'none', 'important');
      }
    }
  }

  private isNestedSubmenu(): boolean {
    return this.el.nativeElement.parentElement?.classList.contains('dropdown-menu') === true;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Open
   */
  @HostListener("mouseenter")
  show(): void {
    this.isShow = true;
    this.el.nativeElement.classList.add('show');

    // For submenus: force correct position immediately via highest-priority inline !important.
    // Bootstrap default (top:100%;left:0) puts the submenu below/inline; we need top:0;left:100%.
    if (this.isNestedSubmenu()) {
      const submenu = this.el.nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
      if (submenu) {
        submenu.style.setProperty('display', 'block', 'important');
        submenu.style.setProperty('position', 'absolute', 'important');
        submenu.style.setProperty('top', '0', 'important');
        submenu.style.setProperty('left', '100%', 'important');
        submenu.style.removeProperty('right');
      }
    } else {
      this.placeTopLevelDropdown();
    }

    this.setSubMenuProp();
  }

  /**
   * El menú horizontal es un carrusel con scroll horizontal, y un contenedor
   * con overflow recorta lo que sobresale: un desplegable absoluto quedaría
   * cortado. El de primer nivel se fija a la ventana (position: fixed) justo
   * debajo de su ítem, y los submenús anidados siguen colgando de él.
   */
  private placeTopLevelDropdown(): void {
    const dropdown = this.el.nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
    if (!dropdown) return;

    const itemRect = (this.el.nativeElement as HTMLElement).getBoundingClientRect();

    dropdown.style.setProperty('position', 'fixed', 'important');
    dropdown.style.setProperty('top', `${itemRect.bottom}px`, 'important');
    dropdown.style.setProperty('left', `${itemRect.left}px`, 'important');
    dropdown.style.setProperty('right', 'auto', 'important');

    // Un ancestro con transform cambia la referencia de `fixed`: se mide dónde
    // quedó realmente y se corrige la diferencia. De paso, si se sale por la
    // derecha de la pantalla se alinea al borde.
    const placed = dropdown.getBoundingClientRect();
    const overflowRight = Math.max(0, itemRect.left + placed.width - (window.innerWidth - 16));
    const left = itemRect.left - (placed.left - itemRect.left) - overflowRight;
    const top = itemRect.bottom - (placed.top - itemRect.bottom);

    dropdown.style.setProperty('left', `${left}px`, 'important');
    dropdown.style.setProperty('top', `${top}px`, 'important');
  }

  /** Devuelve el desplegable de primer nivel a su posición de CSS */
  private resetTopLevelDropdown(): void {
    const dropdown = this.el.nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
    if (!dropdown) return;

    ['position', 'top', 'left', 'right'].forEach(prop => dropdown.style.removeProperty(prop));
  }

  /**
   * Close
   */
  @HostListener("mouseleave")
  hide(): void {
    this.isShow = false;
    this.el.nativeElement.classList.remove('show');
    this.el.nativeElement.classList.remove('openLeft');

    if (this.isNestedSubmenu()) {
      const submenu = this.el.nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
      if (submenu) {
        submenu.style.setProperty('display', 'none', 'important');
        submenu.style.removeProperty('position');
        submenu.style.removeProperty('top');
        submenu.style.removeProperty('left');
        submenu.style.removeProperty('right');
      }
    } else {
      this.resetTopLevelDropdown();
    }
  }

  /**
   * Set sub-menu properties based on screen size
   *
   */
  setSubMenuProp(): void {
    setTimeout(() => {
      const nativeElement = this.el.nativeElement;
      const nativeElementChildren = nativeElement.querySelector(':scope > ul.dropdown-menu') as HTMLElement;
      // If element has sub menu (nested, not top-level)
      if (this.isNestedSubmenu()) {
        const innerHeight = window.innerHeight,
          parentRect = nativeElement.getBoundingClientRect(),
          dropdownHeight = nativeElementChildren.scrollHeight,
          dropdownWidth = nativeElementChildren.offsetWidth;

        // Open sub-menu on left based on screen size (To avoid opening sub-menu outside of the screen)
        if (parentRect.right + dropdownWidth - (window.innerWidth - 16) >= 0) {
          nativeElement.classList.add("openLeft");
          nativeElementChildren.style.setProperty('left', 'auto', 'important');
          nativeElementChildren.style.setProperty('right', '100%', 'important');
        }

        // Set sub-menu height after resolving horizontal position
        const dropdownTop = nativeElementChildren.getBoundingClientRect().top;
        if (innerHeight - dropdownTop - dropdownHeight - 28 < 1) {
          const maxHeight = innerHeight - dropdownTop - 25;
          nativeElementChildren.style.overflowY = 'auto';
          nativeElementChildren.style.overflowX = 'hidden';
          nativeElementChildren.style.maxHeight = maxHeight + 'px';
        }
      }
    });
  }

  /**
   * Confirms if the provided url can be found in one of the given parent's children
   *
   * @param parent
   * @param url
   * @returns {boolean}
   */
  confirmUrlInChildren(parent, url): boolean {
    const children = parent.children;

    // Return false if parent don't have any children
    if (!children) {
      return false;
    }

    // Loop all the children
    for (const child of children) {
      // If children has child (Sub to sub item url)
      if (child.children) {
        // Call function again with child
        if (this.confirmUrlInChildren(child, url)) {
          return true;
        }
      }

      // If child.url is same as provided url
      if (child.url === url || url.includes(child.url)) {
        return true;
      }
    }

    return false;
  }
}
